import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { resolveRangeApiFieldMap, resolveRangeUrlFieldMap } from './fieldMapUtils';
import { getFiniteNumberValue } from './valueUtils';

import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IFilterMiddlewareUtils } from './types';

const parseNumberRangeFilterUrlValue = (
  searchParams: URLSearchParams,
  fieldMap: {
    minField: string;
    maxField: string;
  },
): INumberRangeValue | null => {
  const minStr = isNotEmpty(fieldMap.minField) ? searchParams.get(fieldMap.minField) : null;
  const maxStr = isNotEmpty(fieldMap.maxField) ? searchParams.get(fieldMap.maxField) : null;

  if (isEmpty(minStr) && isEmpty(maxStr)) {
    return null;
  }

  const minNum = getFiniteNumberValue(minStr);
  const maxNum = getFiniteNumberValue(maxStr);
  const hasMin = isValidNumber(minNum);
  const hasMax = isValidNumber(maxNum);

  if (!hasMin && !hasMax) {
    return null;
  }

  return {
    min: hasMin ? (minNum as number) : undefined,
    max: hasMax ? (maxNum as number) : undefined,
  };
};

/**
 * 校验数值是否为有效的有限数字
 */
export const isValidNumber = (val: unknown): val is number => {
  return typeof val === 'number' && Number.isFinite(val);
};

/**
 * 数值范围类筛选器的通用中间件基类
 * 适用于 numberRange / revenueRange 等 { min?: number; max?: number } 类型
 */
export class NumberRangeBaseMiddlewareUtils implements IFilterMiddlewareUtils<INumberRangeValue, IRecord<number>> {
  convertToApiParams(
    item: IFilterItemConfig,
    filterValue?: INumberRangeValue,
    params?: unknown,
  ): IRecord<number> | null {
    if (isEmpty(filterValue)) {
      return null;
    }
    const hasMin = isValidNumber(filterValue.min);
    const hasMax = isValidNumber(filterValue.max);
    if (!hasMin && !hasMax) {
      return null;
    }

    const { minField, maxField } = resolveRangeApiFieldMap(item, params);
    const result: IRecord<number> = {};

    if (hasMin && isNotEmpty(minField)) {
      result[minField] = filterValue.min as number;
    }
    if (hasMax && isNotEmpty(maxField)) {
      result[maxField] = filterValue.max as number;
    }

    return isEmpty(result) ? null : result;
  }

  getManagedUrlKeys(item: IFilterItemConfig, params?: unknown): string[] {
    const { minField, maxField } = resolveRangeUrlFieldMap(item, params);

    return [minField, maxField].filter(isNotEmpty);
  }

  serializeUrlParams(
    item: IFilterItemConfig,
    filterValue?: INumberRangeValue,
    params?: unknown,
  ): IRecord<string> | null {
    if (isEmpty(filterValue)) {
      return null;
    }
    const hasMin = isValidNumber(filterValue.min);
    const hasMax = isValidNumber(filterValue.max);
    if (!hasMin && !hasMax) {
      return null;
    }

    const { minField, maxField } = resolveRangeUrlFieldMap(item, params);
    const result: IRecord<string> = {};

    if (hasMin && isNotEmpty(minField)) {
      result[minField] = String(filterValue.min);
    }
    if (hasMax && isNotEmpty(maxField)) {
      result[maxField] = String(filterValue.max);
    }

    return isEmpty(result) ? null : result;
  }

  deserializeUrlParams(
    item: IFilterItemConfig,
    searchParams: URLSearchParams,
    params?: unknown,
  ): INumberRangeValue | null {
    if (isEmpty(searchParams)) {
      return null;
    }

    const { minField, maxField } = resolveRangeUrlFieldMap(item, params);

    return parseNumberRangeFilterUrlValue(searchParams, {
      minField,
      maxField,
    });
  }
}
